import { FieldProps } from "formik";
import { Autocomplete } from "formik-mui";
import {
  AutocompleteRenderInputParams,
  debounce,
  ListItem,
  ListItemText,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  Abladestelle,
  AbladestellenListe,
  Lieferant,
} from "../../api";
import { useGetAbladestelle, useSucheAbladestellen } from "../../client/hooks";

interface AbladestellenSelectProps extends FieldProps {
  label: string;
  required?: boolean;
  setAbladestelleId: (value: string) => void;
}

function AbladestellenSelect({
  label,
  required,
  setAbladestelleId,
  field,
  form,
  meta,
}: AbladestellenSelectProps) {
  const searchAbladestellen = useSucheAbladestellen();
  const ladeAbladestelle = useGetAbladestelle();
  const [optionen, setOptionen] = useState<ReadonlyArray<Abladestelle>>([]);
  const [inputValue, setInputValue] = useState(field.value);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    form.setFieldValue(field.name, inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (field.value && typeof field.value === "string") {
      ladeAbladestelle(field.value)
        .then((abladestelle) => {
          form.setFieldValue(field.name, abladestelle);
          setInputValue(abladestelle);
        })
        .catch((error) => {
          console.error("Error loading abladestelle:", error);
        });
    }
  }, [field.value]);

  const ladeOptionen = useMemo(
    () =>
      debounce(
        async (
          request: { input: string },
          callback: (error?: ApiError, results?: AbladestellenListe) => void
        ) =>
          searchAbladestellen({
            search: request.input,
            page: 0,
            limit: 20,
            sort: "kurzname",
          })
            .then(async (results) => {
              const priorityIds = JSON.parse(
                localStorage.getItem("letzteAbladestellen") ?? "[]"
              );

              // Fetch all priority items corresponding to priorityIds
              const priorityItems = await Promise.all(
                priorityIds.map(
                  (id: string) =>
                    [...results.items].find((item) => item.id === id) ||
                    ladeAbladestelle(id)
                )
              );
              const aktivePriorityItems = priorityItems.filter(
                (item: Abladestelle) => item.archiviert === null
              );
              // Optionally filter priority items by input
              const filteredPriorityItems =
                request.input.trim() !== ""
                  ? aktivePriorityItems.filter((item: Abladestelle) =>
                      item.kurzname
                        .toLowerCase()
                        .includes(request.input.toLowerCase())
                    )
                  : aktivePriorityItems;

              // Remove duplicates from the final list
              const seenIds = new Set(
                filteredPriorityItems.map((item) => item.id)
              );

              const finalResults = [
                ...filteredPriorityItems,
                ...results.items.filter((item) => !seenIds.has(item.id)),
              ];

              callback(undefined, { ...results, items: finalResults });
            })
            .catch((error) => callback(error)),
        400
      ),
    [searchAbladestellen]
  );

  useEffect(() => {
    ladeOptionen({ input: searchValue }, (error, results) => {
      if (error) console.error("Error:", error);
      setOptionen([...(results ? results.items : [])]);
    });
  }, [searchValue, ladeOptionen]);

  return (
    <Autocomplete
      field={field}
      form={form}
      meta={meta}
      filterSelectedOptions={true}
      filterOptions={(x) => x} // Deaktiviert die Filterlogik, damit komplett nur in dem Backend gesucht werden kann.
      options={optionen}
      getOptionLabel={(option: string | Abladestelle) => {
        if (typeof option === "string") {
          return "";
        } else {
          return option.kurzname;
        }
      }}
      onChange={(event, value) => {
        setInputValue(value);
        const abladestelle = value as Abladestelle;
        if (abladestelle) {
          const abladestelleId = value ? abladestelle.id : "";
          if (abladestelleId !== "") {
            let letzteAbladestellen = JSON.parse(
              localStorage.getItem("letzteAbladestellen") ?? "[]"
            );

            letzteAbladestellen = letzteAbladestellen.filter(
              (id: string) => id !== abladestelleId
            );

            letzteAbladestellen.unshift(abladestelleId);

            if (letzteAbladestellen.length > 10) {
              letzteAbladestellen.splice(0, 10);
            }

            localStorage.setItem(
              "letzteAbladestellen",
              JSON.stringify(letzteAbladestellen)
            );
          }
        }
        if (typeof value === "string") {
          setAbladestelleId(value);
        } else {
          const abladestelle = value as Abladestelle;
          setAbladestelleId(abladestelle?.id);
        }
      }}
      onInputChange={(event, value) => {
        setSearchValue(value);
      }}
      renderOption={(props, option: Abladestelle) => {
        return (
          <ListItem {...props} key={option.id}>
            <ListItemText
              primary={option.kurzname}
              secondary={[option.firma, option.ort].filter(Boolean).join(", ")}
            />
          </ListItem>
        );
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField {...params} label={label + (required ? " *" : "")} />
      )}
    />
  );
}

export default AbladestellenSelect;
